import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  GetAssessmentDeliveriesReceivedQuery,
  GetAssessmentDeliveriesReceivedQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import Layout from "@components/layout/layout";
import AppLink, { useLink } from "@components/link/link";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import {
  assertEdgesNonNull,
  getUrlParam,
  getUrlUsingTypename,
} from "@helpers/helpers";

import getAssessmentDeliveriesReceived from "../graphql/get-assessment-deliveries-received";

const AssessmentsHomepageDeliveries = () => {
  const label = useLabel();
  const location = useLocation();
  const link = useLink();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();

  // Get the userId from the URL
  const userIdUrlParamString = getUrlParam(location, "userId");
  const userIdUrlParam = userIdUrlParamString
    ? parseInt(userIdUrlParamString)
    : null;
  const userNameWhileLoading = "Loading";
  const [userFilter, setUserFilter] = useState<UserComboboxUserOption | null>(
    userIdUrlParam
      ? {
          id: userIdUrlParam,
          name: userNameWhileLoading,
          type: UserComboboxOptionType.USER,
        }
      : {
          id: currentUser.id,
          name: currentUser.name,
          type: UserComboboxOptionType.USER,
        }
  );

  const { options, query, setQuery } = useUserComboboxQuery({
    types: [UserComboboxOptionType.USER],
    selected: userFilter,
  });

  // Update userFilter to the matching option if it's still loading
  useEffect(() => {
    if (userFilter?.name === userNameWhileLoading) {
      const matchingOption = options.find(
        (option) => option.id === userFilter.id
      );
      if (
        matchingOption &&
        matchingOption.type === UserComboboxOptionType.USER
      ) {
        setUserFilter(matchingOption);
      }
    }
  }, [userFilter, options]);

  const { data, loading, fetchMore } = useQuery<
    GetAssessmentDeliveriesReceivedQuery,
    GetAssessmentDeliveriesReceivedQueryVariables
  >(getAssessmentDeliveriesReceived, {
    notifyOnNetworkStatusChange: true,
    variables: {
      organizationId: currentOrganization.id,
      targetId: userFilter?.id,
      after: null,
      orderBy: "-submitted_datetime",
    },
  });
  const deliveries = useMemo(() => {
    const assessmentDeliveries = data?.assessmentDeliveries
      ? assertEdgesNonNull(data?.assessmentDeliveries)
      : [];

    return uniqBy(
      assessmentDeliveries,
      (delivery) => `${delivery.complianceProgram?.id}-${delivery.target?.id}`
    );
  }, [data]);

  const handleClickLoadMore = () => {
    if (data?.assessmentDeliveries?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.assessmentDeliveries?.pageInfo?.endCursor,
          merge: true,
        },
      });
    }
  };

  const handleChangeUserFilter = (option: UserComboboxOption) => {
    if (option.type === UserComboboxOptionType.USER) {
      const params = new URLSearchParams(location.search);
      if (option) {
        params.set("userId", `${option.id}`);
      } else {
        params.delete("userId");
      }
      const url = `${location.pathname}?${params}`;
      link.replace(url);
      setUserFilter(option);
      setQuery("");
    }
  };

  const handleClearUserFilter = () => {
    setUserFilter(null);
    const url = `${location.pathname}`;
    link.replace(url);
  };

  return (
    <>
      <div className="flex items-center gap-3 py-8">
        <div className="text-sm font-medium text-gray-700">
          Show reviews for
        </div>
        <div className="w-64">
          <UserCombobox
            value={userFilter}
            options={options}
            query={query}
            onChangeQuery={setQuery}
            onChangeValue={handleChangeUserFilter}
            clearable={userFilter !== null}
            onClearValue={handleClearUserFilter}
            placeholder={`Filter by person...`}
          />
        </div>
      </div>
      <Layout.MainSection>
        <Layout.MainSubSection
          title={`Past ${label("review", {
            capitalize: true,
            pluralize: true,
          })}`}
          loading={!data && loading}
          emptyPlaceholder={
            deliveries.length === 0 &&
            `No past ${label("review", {
              capitalize: true,
              pluralize: true,
            })}`
          }
        >
          <Layout.MainSubSectionList>
            {deliveries.map((delivery) => (
              <Layout.MainSubSectionListItem key={delivery.id}>
                <div className="flex items-center justify-between text-sm gap-4">
                  <div className="flex items-center gap-3">
                    <Avatar user={delivery.target} size="5" />
                    <div className="flex flex-col gap-0.5">
                      <div className="font-medium">{delivery.target?.name}</div>
                      {delivery.complianceProgram && (
                        <AppLink
                          to={getUrlUsingTypename(delivery)}
                          className="text-gray-500 text-xs font-medium hover:underline"
                        >
                          {delivery.complianceProgram?.title}
                        </AppLink>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-gray-400">
                      {delivery.deliveryDatetime
                        ? `Delivered on ${moment(
                            delivery.deliveryDatetime
                          ).format("MMM Do, YYYY")}`
                        : delivery.lastSubmissionDatetime
                        ? `Submitted on ${moment(
                            delivery.lastSubmissionDatetime
                          ).format("MMM Do, YYYY")}`
                        : ""}
                    </div>
                    <Button text="View" to={getUrlUsingTypename(delivery)} />
                  </div>
                </div>
              </Layout.MainSubSectionListItem>
            ))}
            {data?.assessmentDeliveries?.pageInfo.hasNextPage && (
              <Layout.MainSubSectionListItem className="flex justify-center">
                <Button text="Load more" onClick={handleClickLoadMore} />
              </Layout.MainSubSectionListItem>
            )}
          </Layout.MainSubSectionList>
        </Layout.MainSubSection>
      </Layout.MainSection>
    </>
  );
};

export default AssessmentsHomepageDeliveries;
